import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import API from "../Api";
import { useAuth } from "../context/AuthContext";
import { useLogout } from "../hooks/useLogout";
import "./Dashboard.css";
import "./Alerts.css";
import UserTimezoneSelect from "./UserTimezoneSelect";
import AskCommrayAi from "./AskCommrayAi";

const CATEGORIES = [
  { value: "ERRORS", label: "Errors" },
  { value: "EXCEPTIONS", label: "Exceptions" },
  { value: "OUTAGES", label: "Outages" },
  { value: "HIGH_VOLUME", label: "High volume" },
  { value: "CUSTOM", label: "Custom" }
];

const RULE_TYPES = [
  { value: "NONE", label: "Email template only (no rule)" },
  { value: "ERROR_RATE_PERCENT", label: "Error rate % of traffic" },
  { value: "NO_TRAFFIC", label: "No traffic (outage)" }
];

const ALERT_FIELDS = [
  { id: "index", label: "Index" },
  { id: "application", label: "Application" },
  { id: "sourceUrl", label: "Source URL" },
  { id: "applicationUrl", label: "Application URL" },
  { id: "hostIp", label: "Host IP" },
  { id: "serviceUri", label: "Service URI" },
  { id: "host", label: "Host" },
  { id: "statusCode", label: "Status Code" },
  { id: "exception", label: "Exception" },
  { id: "count", label: "Count" },
  { id: "threshold", label: "Threshold" }
];

const ENV_OPTIONS = [
  { value: "PROD", label: "Prod" },
  { value: "TEST", label: "Test" },
  { value: "QA", label: "QA" },
  { value: "UAT", label: "UAT" }
];

const CLOUD_OPTIONS = [
  { value: "", label: "Not in a public cloud" },
  { value: "AWS", label: "AWS" },
  { value: "AZURE", label: "Azure" },
  { value: "GCP", label: "GCP" }
];

function envLabel(env) {
  return ENV_OPTIONS.find((o) => o.value === env)?.label || "Prod";
}

function cloudLabel(cloud) {
  if (cloud === "AZURE") return "Azure";
  if (cloud === "AWS" || cloud === "GCP") return cloud;
  return "";
}

function orderedFields(ids) {
  const set = new Set(ids || []);
  return ALERT_FIELDS.map((f) => f.id).filter((id) => set.has(id));
}

/** Suggested starters only — every value is editable per application. */
const defaultRuleConfig = () => ({
  type: "ERROR_RATE_PERCENT",
  messageContains: "",
  windowMinutes: 15,
  warningPercent: 2,
  criticalPercent: 10,
  retriggerMinutes: 5,
  scope: "SINGLE",
  cloud: "",
  environment: "PROD",
  includedFields: ALERT_FIELDS.map((f) => f.id),
  index: "",
  applicationName: "",
  sourceUrl: "",
  applicationUrl: "",
  hostIp: ""
});

function buildSubjectTemplate(rule) {
  const env = envLabel(rule?.environment || "PROD");
  const cloud = cloudLabel(rule?.cloud || "");
  const cloudPart = cloud ? `${cloud} | ` : "";
  return `Commray monitoring alert: ${cloudPart}{{workspaceName}} | {{alertName}} | ${env} | {{severity}}`;
}

function buildBodyTemplate(rule) {
  const fields = ALERT_FIELDS.filter((f) => (rule?.includedFields || []).includes(f.id));
  const group = rule?.scope === "GROUP";
  const lines = [
    "Time: {{timestamp}}",
    "Workspace: {{workspaceName}}",
    "Alert: {{alertName}}",
    "Severity: {{severity}}",
    "",
    "{{summary}}",
    ""
  ];
  if (group) {
    lines.push("All applications in this workspace:", "");
    if (!fields.length) {
      lines.push("(No columns selected)");
    } else {
      lines.push(fields.map((f) => f.label).join(" | "));
      lines.push("{{groupRows}}");
    }
  } else {
    lines.push("Application:", "");
    if (!fields.length) {
      lines.push("(No fields selected)");
    } else {
      fields.forEach((f) => lines.push(`${f.label}: {{${f.id}}}`));
    }
  }
  lines.push("", "{{details}}", "", "View results in Commray logs:", "{{logsUrl}}");
  return lines.join("\n");
}

const emptyTemplateForm = () => {
  const ruleConfig = defaultRuleConfig();
  return {
    name: "",
    category: "ERRORS",
    subjectTemplate: buildSubjectTemplate(ruleConfig),
    bodyTemplate: buildBodyTemplate(ruleConfig),
    bodyCustomized: false,
    enabled: true,
    ruleConfig
  };
};

function sourceAppName(source) {
  return String(source?.applicationName || source?.applications?.[0] || "").trim();
}

function uniqueValues(values) {
  const seen = new Set();
  const out = [];
  values.forEach((value) => {
    const text = String(value || "").trim();
    if (!text || seen.has(text)) return;
    seen.add(text);
    out.push(text);
  });
  return out;
}

function isHostIp(value) {
  const text = String(value || "").trim();
  if (!text) return true;
  if (/^(?:\d{1,3}\.){3}\d{1,3}$/.test(text)) {
    return text.split(".").every((part) => Number(part) <= 255);
  }
  return /^[0-9A-Fa-f:]+$/.test(text) && text.includes(":");
}

function formatRuleSummary(rule) {
  const scope = rule?.scope === "GROUP" ? "Group" : "Single app";
  if (!rule || !rule.type || rule.type === "NONE") return `${scope} · no evaluation rule`;
  if (rule.type === "NO_TRAFFIC") {
    return `${scope} · no traffic for ${rule.windowMinutes ?? "?"}m · retrigger every ${rule.retriggerMinutes ?? "?"}m`;
  }
  if (rule.type === "ERROR_RATE_PERCENT") {
    return `${scope} · “${rule.messageContains || "…"}” · ${rule.windowMinutes ?? "?"}m · warn ≥${rule.warningPercent ?? "?"}% · critical ≥${rule.criticalPercent ?? "?"}% · retrigger ${rule.retriggerMinutes ?? "?"}m`;
  }
  return `${scope} · ${rule.type}`;
}

function Alerts() {
  const navigate = useNavigate();
  const { canUsePremium, startCheckout } = useAuth();
  const handleLogout = useLogout();
  const [darkMode, setDarkMode] = useState(localStorage.getItem("darkMode") === "true");
  const [userInfo, setUserInfo] = useState({});
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const [recipientEmails, setRecipientEmails] = useState("");
  const [emailConfigured, setEmailConfigured] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveMessage, setSaveMessage] = useState(null);
  const [error, setError] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyTemplateForm());
  const [placeholders, setPlaceholders] = useState({});
  const [logSources, setLogSources] = useState([]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [settingsRes, templatesRes, phRes, sourcesRes] = await Promise.all([
        API.get("/api/alerts/settings"),
        API.get("/api/alerts/templates"),
        API.get("/api/alerts/placeholders"),
        API.get("/api/monitoring/log-sources").catch(() => ({ data: [] }))
      ]);
      setRecipientEmails(settingsRes.data.recipientEmails || "");
      setEmailConfigured(!!settingsRes.data.emailConfigured);
      setTemplates(templatesRes.data || []);
      setPlaceholders(phRes.data || {});
      setLogSources(Array.isArray(sourcesRes.data) ? sourcesRes.data : []);
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.error || e.message || "Failed to load alerts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await API.get("/auth/user-info");
        if (res.data.username) {
          setUserInfo({
            username: res.data.username,
            role: res.data.role || "USER"
          });
          if (res.data.themePreference === "dark") setDarkMode(true);
        }
      } catch {
        setUserInfo({
          username: localStorage.getItem("username") || "User",
          role: localStorage.getItem("userRole") || "USER"
        });
      }
    };
    fetchUser();
  }, []);

  useEffect(() => {
    const onOutside = (ev) => {
      if (dropdownOpen && !ev.target.closest(".user-dropdown")) setDropdownOpen(false);
      if (settingsOpen && !ev.target.closest(".settings-dropdown")) setSettingsOpen(false);
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, [dropdownOpen, settingsOpen]);

  const [upgradeBusy, setUpgradeBusy] = useState(false);
  const handleUpgrade = async () => {
    if (upgradeBusy) return;
    setUpgradeBusy(true);
    try {
      const result = await startCheckout("starter");
      if (result?.message && !result?.url) {
        setError(result.message);
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Could not start checkout.");
    } finally {
      setUpgradeBusy(false);
    }
  };

  const saveRecipients = async () => {
    setSaveMessage(null);
    try {
      const res = await API.put("/api/alerts/settings", { recipientEmails });
      setRecipientEmails(res.data.recipientEmails || "");
      setEmailConfigured(!!res.data.emailConfigured);
      setSaveMessage("Recipients saved.");
    } catch (e) {
      setSaveMessage(e.response?.data?.error || e.message || "Save failed");
    }
  };

  const installSamples = async () => {
    setSaveMessage(null);
    setError(null);
    try {
      const res = await API.post("/api/alerts/templates/install-samples");
      setSaveMessage(res.data.message || "Samples installed.");
      await loadAll();
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Failed to install samples");
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyTemplateForm());
    setModalOpen(true);
  };

  const openEdit = (t) => {
    const ruleConfig = {
      ...defaultRuleConfig(),
      ...(t.ruleConfig || {}),
      type: t.ruleConfig?.type || "NONE",
      scope: t.ruleConfig?.scope === "GROUP" ? "GROUP" : "SINGLE",
      cloud: t.ruleConfig?.cloud || "",
      environment: t.ruleConfig?.environment || "PROD",
      includedFields: orderedFields(
        t.ruleConfig?.includedFields?.length ? t.ruleConfig.includedFields : ALERT_FIELDS.map((f) => f.id)
      )
    };
    const generated = buildBodyTemplate(ruleConfig);
    setEditingId(t.id);
    setForm({
      name: t.name,
      category: t.category,
      subjectTemplate: buildSubjectTemplate(ruleConfig),
      bodyTemplate: t.bodyTemplate || generated,
      bodyCustomized: (t.bodyTemplate || "") !== generated,
      enabled: t.enabled,
      ruleConfig
    });
    setModalOpen(true);
  };

  const applyRule = (ruleConfig, { resetBody = false } = {}) => {
    setForm((f) => {
      const subjectTemplate = buildSubjectTemplate(ruleConfig);
      const generated = buildBodyTemplate(ruleConfig);
      const keepCustom = !resetBody && f.bodyCustomized;
      return {
        ...f,
        ruleConfig,
        subjectTemplate,
        bodyTemplate: keepCustom ? f.bodyTemplate : generated,
        bodyCustomized: keepCustom
      };
    });
  };

  const setRuleField = (field, value) => {
    applyRule({ ...form.ruleConfig, [field]: value });
  };

  const toggleIncludedField = (id) => {
    const current = form.ruleConfig?.includedFields || [];
    const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
    applyRule({ ...form.ruleConfig, includedFields: orderedFields(next) });
  };

  const resetBodyFromOptions = () => {
    applyRule(form.ruleConfig, { resetBody: true });
  };

  const selectSourceIndex = (index) => {
    const applicationName = uniqueValues(
      logSources.filter((source) => !index || source.indexName === index).map(sourceAppName)
    ).includes(form.ruleConfig?.applicationName)
      ? form.ruleConfig.applicationName
      : "";
    const urls = uniqueValues(logSources.filter((source) => {
      if (index && source.indexName !== index) return false;
      if (applicationName && sourceAppName(source) !== applicationName) return false;
      return Boolean(source.endpoint);
    }).map((source) => source.endpoint));
    const sourceUrl = urls.includes(form.ruleConfig?.sourceUrl) ? form.ruleConfig.sourceUrl : "";
    const previousUrl = form.ruleConfig?.sourceUrl || "";
    const applicationUrl = sourceUrl
      || (form.ruleConfig?.applicationUrl && form.ruleConfig.applicationUrl !== previousUrl
        ? form.ruleConfig.applicationUrl
        : "");
    applyRule({
      ...form.ruleConfig,
      index,
      applicationName,
      sourceUrl,
      applicationUrl
    });
  };

  const selectSourceApp = (applicationName) => {
    const index = form.ruleConfig?.index || "";
    const match = logSources.find((source) =>
      sourceAppName(source) === applicationName && (!index || source.indexName === index));
    const nextIndex = index || match?.indexName || "";
    const urls = uniqueValues(logSources.filter((source) => {
      if (nextIndex && source.indexName !== nextIndex) return false;
      if (applicationName && sourceAppName(source) !== applicationName) return false;
      return Boolean(source.endpoint);
    }).map((source) => source.endpoint));
    const sourceUrl = urls.length === 1
      ? urls[0]
      : (urls.includes(form.ruleConfig?.sourceUrl) ? form.ruleConfig.sourceUrl : "");
    applyRule({
      ...form.ruleConfig,
      index: nextIndex,
      applicationName,
      sourceUrl,
      applicationUrl: sourceUrl || form.ruleConfig?.applicationUrl || ""
    });
  };

  const selectSourceUrl = (sourceUrl) => {
    const match = logSources.find((source) => source.endpoint === sourceUrl);
    applyRule({
      ...form.ruleConfig,
      sourceUrl,
      index: match?.indexName || form.ruleConfig?.index || "",
      applicationName: match ? sourceAppName(match) : (form.ruleConfig?.applicationName || ""),
      applicationUrl: sourceUrl || form.ruleConfig?.applicationUrl || ""
    });
  };

  const saveTemplate = async (e) => {
    e.preventDefault();
    setError(null);
    const rule = form.ruleConfig || defaultRuleConfig();
    const applicationUrl = String(rule.applicationUrl || rule.sourceUrl || "").trim();
    const hostIp = String(rule.hostIp || "").trim();
    if (applicationUrl && !/^https?:\/\/\S+$/i.test(applicationUrl)) {
      setError("Application URL must start with http:// or https://");
      return;
    }
    if (hostIp && !isHostIp(hostIp)) {
      setError("Host IP must be an IPv4 or IPv6 address");
      return;
    }
    const payload = {
      name: form.name,
      category: form.category,
      subjectTemplate: buildSubjectTemplate(rule),
      bodyTemplate: form.bodyTemplate,
      enabled: form.enabled,
      ruleConfig: {
        type: rule.type || "NONE",
        messageContains: rule.messageContains || "",
        windowMinutes: rule.windowMinutes === "" || rule.windowMinutes == null ? null : Number(rule.windowMinutes),
        warningPercent: rule.warningPercent === "" || rule.warningPercent == null ? null : Number(rule.warningPercent),
        criticalPercent: rule.criticalPercent === "" || rule.criticalPercent == null ? null : Number(rule.criticalPercent),
        retriggerMinutes: rule.retriggerMinutes === "" || rule.retriggerMinutes == null ? null : Number(rule.retriggerMinutes),
        scope: rule.scope === "GROUP" ? "GROUP" : "SINGLE",
        cloud: rule.cloud || "",
        environment: rule.environment || "PROD",
        includedFields: orderedFields(rule.includedFields),
        index: rule.index || "",
        applicationName: rule.applicationName || "",
        sourceUrl: rule.sourceUrl || "",
        applicationUrl,
        hostIp
      }
    };
    try {
      if (editingId) {
        await API.put(`/api/alerts/templates/${editingId}`, payload);
      } else {
        await API.post("/api/alerts/templates", payload);
      }
      setModalOpen(false);
      await loadAll();
    } catch (err) {
      const msg = err.response?.data?.error || err.message || "Save failed";
      setError(msg);
    }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm("Delete this alert template?")) return;
    try {
      await API.delete(`/api/alerts/templates/${id}`);
      await loadAll();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const testSend = async (id) => {
    setSaveMessage(null);
    try {
      const res = await API.post(`/api/alerts/templates/${id}/test-send`);
      if (res.data.sent) {
        setSaveMessage(`Test email sent to: ${(res.data.recipients || []).join(", ")}`);
      } else {
        setSaveMessage(
          res.data.reason ||
            `SMTP not active. Preview — Subject: ${res.data.previewSubject?.slice(0, 80)}…`
        );
      }
    } catch (e) {
      setSaveMessage(e.response?.data?.error || e.message || "Test send failed");
    }
  };

  const toggleDarkMode = async () => {
    const next = !darkMode;
    const theme = next ? "dark" : "light";
    try {
      const response = await API.post("/auth/update-theme", { theme });
      if (response.data?.message) {
        setDarkMode(next);
        localStorage.setItem("darkMode", next.toString());
      } else {
        setDarkMode(next);
        localStorage.setItem("darkMode", next.toString());
      }
      setSettingsOpen(false);
    } catch {
      setDarkMode(next);
      localStorage.setItem("darkMode", next.toString());
      setSettingsOpen(false);
    }
  };

  return (
    <div className={`dashboard-container dashboards-page alerts-page ${darkMode ? "dark-mode" : "light-mode"}`}>
      <header className="dashboard-header">
        <div className="header-content">
          <div className="brand-section">
            <h1 className="brand-name brand-clickable" onClick={() => navigate("/dashboards")} role="button" tabIndex={0}>
              Commray
            </h1>
          </div>
          <div className="center-title">
            <h2 className="dashboard-title-header">Alerts</h2>
          </div>
          <div className="right-controls">
            <AskCommrayAi />
            <nav className="header-alerts-nav">
              <button type="button" className="header-nav-link" onClick={() => navigate("/dashboards")}>
                Dashboards
              </button>
              <button type="button" className="header-nav-link" onClick={() => navigate("/logs")}>
                Logs
              </button>
            </nav>
            <div className="settings-dropdown">
              <button type="button" className="settings-button" onClick={() => { setSettingsOpen(!settingsOpen); setDropdownOpen(false); }}>
                <span className="settings-icon">⚙️</span>
              </button>
              {settingsOpen && (
                <div className="settings-menu">
                  <div className="settings-header">
                    <h4>Settings</h4>
                  </div>
                  <div className="settings-divider" />
                  <div className="settings-item" onClick={toggleDarkMode}>
                    <span className="settings-item-icon">{darkMode ? "☀️" : "🌙"}</span>
                    <span>{darkMode ? "Light Mode" : "Dark Mode"}</span>
                  </div>
                  <UserTimezoneSelect />
                </div>
              )}
            </div>
            <div className="user-dropdown">
              <button type="button" className="user-button" onClick={() => { setDropdownOpen(!dropdownOpen); setSettingsOpen(false); }}>
                <div className="user-avatar">
                  {userInfo.username ? userInfo.username.charAt(0).toUpperCase() : "U"}
                </div>
                <div className="user-info">
                  <span className="user-name">{userInfo.username}</span>
                  <span className="user-role">{userInfo.role?.replace("ROLE_", "")}</span>
                </div>
                <div className={`dropdown-arrow ${dropdownOpen ? "open" : ""}`}>▼</div>
              </button>
              {dropdownOpen && (
                <div className="dropdown-menu">
                  <div className="dropdown-header">
                    <div className="dropdown-user-info">
                      <div className="dropdown-avatar">{userInfo.username?.charAt(0).toUpperCase()}</div>
                      <div>
                        <div className="dropdown-name">{userInfo.username}</div>
                        <div className="dropdown-role">{userInfo.role?.replace("ROLE_", "")}</div>
                      </div>
                    </div>
                  </div>
                  <div className="dropdown-divider" />
                  <div className="dropdown-item" onClick={() => { setDropdownOpen(false); navigate("/settings/profile"); }}>
                    <span className="dropdown-icon">👤</span>
                    Settings
                  </div>
                  <div className="dropdown-item" onClick={() => { setDropdownOpen(false); navigate("/change-password"); }}>
                    <span className="dropdown-icon">🔒</span>
                    Change Password
                  </div>
                  <div className="dropdown-divider" />
                  <div className="dropdown-item logout-item" onClick={handleLogout}>
                    <span className="dropdown-icon">🚪</span>
                    Logout
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="dashboard-content alerts-main">
        <div className="dashboard-title">
          <h2>Alerting &amp; notifications</h2>
          <p>
            Choose who receives alert emails and define templates with{" "}
            <code className="alerts-inline-code">{"{{placeholder}}"}</code> fields. Configure match text, time
            window, warning/critical percentages, and retrigger interval per application — nothing is
            product-hardcoded. Paid plan or active trial required.
          </p>
        </div>

        {error && <div className="alerts-banner alerts-banner--error">{error}</div>}
        {saveMessage && <div className="alerts-banner alerts-banner--info">{saveMessage}</div>}

        {!canUsePremium && (
          <section className="alert-section-card">
            <div className="alert-section-header">
              <h3>Upgrade required</h3>
            </div>
            <p className="alert-section-hint">
              Alerts templates and notification delivery are available on paid plans or active trials.
            </p>
            <div className="alerts-row-actions">
              <button type="button" className="action-button create" onClick={handleUpgrade} disabled={upgradeBusy}>
                {upgradeBusy ? "Starting…" : "Upgrade now"}
              </button>
            </div>
          </section>
        )}

        {canUsePremium && (
        <section className="alert-section-card">
          <div className="alert-section-header">
            <h3>Notification recipients</h3>
            <span className={`alerts-status-pill ${emailConfigured ? "alerts-status-pill--ok" : "alerts-status-pill--warn"}`}>
              {emailConfigured ? "SMTP configured" : "SMTP not configured — save templates & recipients; configure server to send"}
            </span>
          </div>
          <p className="alert-section-hint">
            Enter one or more addresses separated by commas, semicolons, or new lines. Commray sends alerts to these
            emails when integrated monitors trigger (and for test sends from this page).
          </p>
          <textarea
            className="alerts-textarea"
            rows={4}
            value={recipientEmails}
            onChange={(e) => setRecipientEmails(e.target.value)}
            placeholder="ops@company.com, oncall@company.com"
          />
          <div className="alerts-row-actions">
            <button type="button" className="action-button primary" onClick={saveRecipients}>
              Save recipients
            </button>
          </div>
        </section>
        )}

        {canUsePremium && (
        <section className="alert-section-card">
          <div className="alert-section-header">
            <h3>Available placeholders</h3>
          </div>
          <dl className="alerts-placeholder-list">
            {Object.entries(placeholders).map(([k, desc]) => (
              <div key={k} className="alerts-placeholder-row">
                <dt>
                  <code>{"{{" + k + "}}"}</code>
                </dt>
                <dd>{desc}</dd>
              </div>
            ))}
          </dl>
        </section>
        )}

        {canUsePremium && (
        <section className="alert-section-card">
          <div className="alert-section-header">
            <h3>Alert templates</h3>
            <div className="alerts-row-actions" style={{ margin: 0 }}>
              <button
                type="button"
                className="action-button secondary alerts-add-btn"
                onClick={installSamples}
                disabled={loading}
                title="Install starter templates you can edit (match, window, %, retrigger)"
              >
                Install starter samples
              </button>
              <button type="button" className="action-button secondary alerts-add-btn" onClick={openCreate} disabled={loading}>
                + New template
              </button>
            </div>
          </div>
          <p className="alert-section-hint">
            Starters prefill example match strings (e.g. <code className="alerts-inline-code">statusCode: 503</code>)
            and suggested numbers. Edit each template so window, warning %, critical %, and retrigger match that
            application&apos;s behaviour.
          </p>

          {loading ? (
            <p className="alerts-muted">Loading…</p>
          ) : templates.length === 0 ? (
            <p className="alerts-muted">
              No templates yet. Use <strong>Install starter samples</strong> or create one and set your own thresholds.
            </p>
          ) : (
            <ul className="alerts-template-list">
              {templates.map((t) => (
                <li key={t.id} className="alerts-template-item">
                  <div className="alerts-template-main">
                    <span className="alerts-template-name">{t.name}</span>
                    <span className="alerts-template-cat">{t.category}</span>
                    {!t.enabled && <span className="alerts-badge-disabled">Disabled</span>}
                  </div>
                  <div className="alerts-template-subject">{formatRuleSummary(t.ruleConfig)}</div>
                  <div className="alerts-template-subject">Subject: {t.subjectTemplate}</div>
                  <div className="alerts-template-actions">
                    <button type="button" className="alerts-btn-text" onClick={() => testSend(t.id)}>Test send</button>
                    <button type="button" className="alerts-btn-text" onClick={() => openEdit(t)}>Edit</button>
                    <button type="button" className="alerts-btn-text alerts-btn-danger" onClick={() => deleteTemplate(t.id)}>Delete</button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
        )}
      </main>

      {modalOpen && (
        <div className="alerts-modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="alerts-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="alerts-modal-title">
            <div className="alerts-modal-header">
              <h3 id="alerts-modal-title">{editingId ? "Edit template" : "New template"}</h3>
              <button type="button" className="alerts-modal-close" onClick={() => setModalOpen(false)} aria-label="Close">×</button>
            </div>
            <form onSubmit={saveTemplate} className="alerts-modal-body">
              <label className="alerts-field">
                <span>Name</span>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  required
                  maxLength={200}
                />
              </label>
              <label className="alerts-field">
                <span>Category</span>
                <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </label>

              <fieldset className="alerts-rule-fieldset">
                <legend>Evaluation rule (per application)</legend>
                <p className="alert-section-hint" style={{ marginTop: 0 }}>
                  Set match text, lookback window, warning/critical share of traffic, and how often to re-email
                  while the issue continues. Adjust for each app — starters are suggestions only.
                </p>
                <label className="alerts-field">
                  <span>Rule type</span>
                  <select
                    value={form.ruleConfig?.type || "NONE"}
                    onChange={(e) => setRuleField("type", e.target.value)}
                  >
                    {RULE_TYPES.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </label>

                {form.ruleConfig?.type === "ERROR_RATE_PERCENT" && (
                  <>
                    <label className="alerts-field">
                      <span>Match in message (e.g. statusCode: 503)</span>
                      <input
                        type="text"
                        value={form.ruleConfig.messageContains || ""}
                        onChange={(e) => setRuleField("messageContains", e.target.value)}
                        placeholder="statusCode: 503"
                        required
                      />
                    </label>
                    <div className="alerts-rule-grid">
                      <label className="alerts-field">
                        <span>Window (minutes)</span>
                        <input
                          type="number"
                          min={1}
                          step={1}
                          value={form.ruleConfig.windowMinutes ?? ""}
                          onChange={(e) => setRuleField("windowMinutes", e.target.value)}
                          required
                        />
                      </label>
                      <label className="alerts-field">
                        <span>Warning ≥ %</span>
                        <input
                          type="number"
                          min={0}
                          step={0.1}
                          value={form.ruleConfig.warningPercent ?? ""}
                          onChange={(e) => setRuleField("warningPercent", e.target.value)}
                          required
                        />
                      </label>
                      <label className="alerts-field">
                        <span>Critical ≥ %</span>
                        <input
                          type="number"
                          min={0}
                          step={0.1}
                          value={form.ruleConfig.criticalPercent ?? ""}
                          onChange={(e) => setRuleField("criticalPercent", e.target.value)}
                          required
                        />
                      </label>
                      <label className="alerts-field">
                        <span>Retrigger every (minutes)</span>
                        <input
                          type="number"
                          min={1}
                          step={1}
                          value={form.ruleConfig.retriggerMinutes ?? ""}
                          onChange={(e) => setRuleField("retriggerMinutes", e.target.value)}
                          required
                        />
                      </label>
                    </div>
                  </>
                )}

                {form.ruleConfig?.type === "NO_TRAFFIC" && (
                  <>
                  <p className="alert-section-hint" style={{ marginTop: 0 }}>
                    Group scope emails when any application in this workspace sends no logs during the window.
                    Single application emails only when that selected app is completely silent.
                  </p>
                  <div className="alerts-rule-grid">
                    <label className="alerts-field">
                      <span>No traffic for (minutes)</span>
                      <input
                        type="number"
                        min={1}
                        step={1}
                        value={form.ruleConfig.windowMinutes ?? ""}
                        onChange={(e) => setRuleField("windowMinutes", e.target.value)}
                        required
                      />
                    </label>
                    <label className="alerts-field">
                      <span>Retrigger every (minutes)</span>
                      <input
                        type="number"
                        min={1}
                        step={1}
                        value={form.ruleConfig.retriggerMinutes ?? ""}
                        onChange={(e) => setRuleField("retriggerMinutes", e.target.value)}
                        required
                      />
                    </label>
                  </div>
                  </>
                )}
              </fieldset>

              <fieldset className="alerts-rule-fieldset">
                <legend>Application</legend>
                <p className="alert-section-hint" style={{ marginTop: 0 }}>
                  Choose an index, app name, and source URL from log sources already in this workspace.
                  Application URL and host IP are optional.
                </p>
                <div className="alerts-rule-grid">
                  <label className="alerts-field">
                    <span>Index</span>
                    <select
                      value={form.ruleConfig?.index || ""}
                      onChange={(e) => selectSourceIndex(e.target.value)}
                    >
                      <option value="">Select index</option>
                      {uniqueValues([...(logSources.map((source) => source.indexName)), form.ruleConfig?.index]).map((index) => (
                        <option key={index} value={index}>{index}</option>
                      ))}
                    </select>
                  </label>
                  <label className="alerts-field">
                    <span>App name</span>
                    <select
                      value={form.ruleConfig?.applicationName || ""}
                      onChange={(e) => selectSourceApp(e.target.value)}
                    >
                      <option value="">Select app</option>
                      {uniqueValues(logSources
                        .filter((source) => !form.ruleConfig?.index || source.indexName === form.ruleConfig.index)
                        .map(sourceAppName)
                        .concat(form.ruleConfig?.applicationName || "")).map((name) => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                  </label>
                  <label className="alerts-field alerts-field-wide">
                    <span>Source URL</span>
                    <select
                      value={form.ruleConfig?.sourceUrl || ""}
                      onChange={(e) => selectSourceUrl(e.target.value)}
                    >
                      <option value="">Select source URL</option>
                      {uniqueValues(logSources
                        .filter((source) => {
                          if (form.ruleConfig?.index && source.indexName !== form.ruleConfig.index) return false;
                          if (form.ruleConfig?.applicationName && sourceAppName(source) !== form.ruleConfig.applicationName) return false;
                          return Boolean(source.endpoint);
                        })
                        .map((source) => source.endpoint)
                        .concat(form.ruleConfig?.sourceUrl || "")).map((url) => (
                        <option key={url} value={url}>{url}</option>
                      ))}
                    </select>
                  </label>
                  <label className="alerts-field">
                    <span>Host IP</span>
                    <input
                      type="text"
                      value={form.ruleConfig?.hostIp || ""}
                      onChange={(e) => setRuleField("hostIp", e.target.value)}
                      placeholder="203.0.113.10"
                      spellCheck={false}
                    />
                  </label>
                </div>
                <label className="alerts-field">
                  <span>Application URL</span>
                  <input
                    type="url"
                    value={form.ruleConfig?.applicationUrl || ""}
                    onChange={(e) => setRuleField("applicationUrl", e.target.value)}
                    placeholder="https://billing.example.com"
                    spellCheck={false}
                  />
                </label>
                {logSources.length === 0 && (
                  <p className="alert-section-hint">No log sources in this workspace yet. Add them in Log Explorer, then they appear in these lists.</p>
                )}
              </fieldset>

              <fieldset className="alerts-rule-fieldset">
                <legend>Email layout</legend>
                <p className="alert-section-hint" style={{ marginTop: 0 }}>
                  Subject always starts with <code className="alerts-inline-code">Commray monitoring alert:</code> and
                  includes cloud (when set), workspace, alert name, environment, and severity.
                </p>
                <div className="alerts-scope-row">
                  <label className="alerts-field alerts-field-checkbox">
                    <input
                      type="radio"
                      name="alert-scope"
                      checked={form.ruleConfig?.scope !== "GROUP"}
                      onChange={() => setRuleField("scope", "SINGLE")}
                    />
                    <span>Single application</span>
                  </label>
                  <label className="alerts-field alerts-field-checkbox">
                    <input
                      type="radio"
                      name="alert-scope"
                      checked={form.ruleConfig?.scope === "GROUP"}
                      onChange={() => setRuleField("scope", "GROUP")}
                    />
                    <span>Group — all apps in the workspace</span>
                  </label>
                </div>
                <div className="alerts-rule-grid">
                  <label className="alerts-field">
                    <span>Cloud</span>
                    <select
                      value={form.ruleConfig?.cloud || ""}
                      onChange={(e) => setRuleField("cloud", e.target.value)}
                    >
                      {CLOUD_OPTIONS.map((c) => (
                        <option key={c.value || "none"} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </label>
                  <label className="alerts-field">
                    <span>Environment</span>
                    <select
                      value={form.ruleConfig?.environment || "PROD"}
                      onChange={(e) => setRuleField("environment", e.target.value)}
                    >
                      {ENV_OPTIONS.map((c) => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <p className="alert-section-hint">
                  {form.ruleConfig?.scope === "GROUP"
                    ? "Optional columns for the group table (one row per application)."
                    : "Fields listed for this single application."}
                </p>
                <div className="alerts-field-checks">
                  {ALERT_FIELDS.map((field) => (
                    <label key={field.id} className="alerts-field alerts-field-checkbox">
                      <input
                        type="checkbox"
                        checked={(form.ruleConfig?.includedFields || []).includes(field.id)}
                        onChange={() => toggleIncludedField(field.id)}
                      />
                      <span>{field.label}</span>
                    </label>
                  ))}
                </div>
              </fieldset>

              <label className="alerts-field">
                <span>Subject</span>
                <input
                  type="text"
                  value={form.subjectTemplate}
                  readOnly
                  maxLength={500}
                />
              </label>
              <label className="alerts-field">
                <span>Message body</span>
                <textarea
                  rows={12}
                  value={form.bodyTemplate}
                  onChange={(e) => setForm((f) => ({
                    ...f,
                    bodyTemplate: e.target.value,
                    bodyCustomized: true
                  }))}
                  required
                />
              </label>
              <div className="alerts-row-actions">
                <button type="button" className="alerts-btn-text" onClick={resetBodyFromOptions}>
                  Reset message from selected fields
                </button>
              </div>
              <p className="alert-section-hint">
                Emails include a link to view results in Commray logs (<code className="alerts-inline-code">{"{{logsUrl}}"}</code>).
              </p>
              <label className="alerts-field alerts-field-checkbox">
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
                />
                <span>Enabled (hold/pause by unchecking — keeps the template)</span>
              </label>
              <div className="alerts-modal-footer">
                <button type="button" className="action-button secondary" onClick={() => setModalOpen(false)}>Cancel</button>
                <button type="submit" className="action-button primary">Save template</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Alerts;
